import { BrowserHistory } from '@wix/devzai-common-client';
import { InitializableValue, evaluateFunction } from '@wix/devzai-utils-common';
import {
    BymoPageProps,
    bymoPagePropsGetInnerPageBySlug,
    DoppeViewerPageInfo,
    DoppeViewerPageType,
    doppeViewerPageInfoCreateHome
} from '../../client-server-common';
import { DoppeViewerClientServices } from '../doppe-user-app/doppe-viewer-client-services';
import { bymoEditorCurrentPageScopeGetPageId } from '../components/bymo-editor-current-page-scope/bymo-editor-current-page-scope-utils';

const DisableHistory = true;

/**
 * Navigation options for router methods
 */
export interface DoppeViewerRouterNavigationOptions {
    /**
     * Replace history instead of push (default: false)
     */
    replace?: boolean;
    
    /**
     * Update URL in browser (default: true)
     */
    updateUrl?: boolean;
}

/**
 * Viewer Router Client Service
 * 
 * Manages navigation and browser history for the viewer application.
 * Uses DoppeViewerPagesRoutesManager for URL resolution and generation.
 * 
 * Features:
 * - Type-safe navigation methods for each page type
 * - Browser history management (back/forward buttons)
 * - URL resolution (convert page info ↔ URL)
 * - Deep linking support
 * 
 * @example
 * ```typescript
 * // Initialize
 * router.initialize({
 *     bymoPageProps: bymoPageProps,
 *     onPageChange: (pageInfo) => {
 *         // Handle page change
 *     },
 *     initialPageInfo: homePageInfo
 * });
 * 
 * // Navigate
 * router.navigateToProductItem('products-page-id', 'my-product-slug');
 * 
 * // Get URL
 * const url = router.getPageUrl(productItemPageInfo);
 * ```
 */
export class DoppeViewerRouterClientService {
    
    private bymoPageProps?: BymoPageProps;
    private onPageChangeCallbacks: Array<(pageInfo: DoppeViewerPageInfo) => void> = [];
    private currentPageInfo?: DoppeViewerPageInfo;
    private urlMode: 'hash' | 'path';
    private browserHistoryUnlisten?: () => void;
    private isPreviewMode: boolean = false;
    
    constructor(
        private deferredClientServices: InitializableValue<DoppeViewerClientServices>,
        options: {
            urlMode?: 'hash' | 'path';  // Default: 'hash'
        }
    ) {
        this.urlMode = options.urlMode ?? 'path';
        
        // Routes manager is accessed through client services
        // Viewer routes are always root-relative (no basePath needed)
        // SSR-safe - no window or browser API access during construction
    }
    
    /**
     * Access routes manager through client services
     * Follows the dashboard pattern for separation of concerns
     */
    private get routesManager() {
        return this.deferredClientServices.getValue().getViewerPagesRoutesManager();
    }
    
    // ========================================================================
    // Initialization & Cleanup
    // ========================================================================
    
    /**
     * Initialize the router service
     * 
     * @param options - Initialization options
     * @param options.bymoPageProps - Page props for route resolution
     * @param options.onPageChange - Callback when page changes
     * @param options.initialPageInfo - Initial page info (optional)
     * @param options.isPreviewMode - Whether this router is for a preview (skips BrowserHistory operations)
     */
    public initialize(options: {
        bymoPageProps: BymoPageProps;
        onPageChange?: (pageInfo: DoppeViewerPageInfo) => void;
        initialPageInfo?: DoppeViewerPageInfo;
        isPreviewMode?: boolean;
    }): void {
        const { bymoPageProps: bymoPagePropsValue, onPageChange: onPageChangeValue, initialPageInfo: initialPageInfoValue, isPreviewMode = false } = options;
        
        // Set bymoPageProps first so resolvePageInfoFromUrl can work correctly
        this.bymoPageProps = bymoPagePropsValue;

        this.isPreviewMode = isPreviewMode;
        
        if (onPageChangeValue) {
            this.onPageChangeCallbacks.push(onPageChangeValue);
        }
        
        // If no initial page info provided, resolve from current URL
        // Now that bymoPageProps is set, this will work correctly for all page types
        this.currentPageInfo = initialPageInfoValue ?? doppeViewerPageInfoCreateHome();
        
        // Skip BrowserHistory operations in preview mode
        // Previews should not affect browser history or trigger navigation events
        if (isPreviewMode || DisableHistory) {
            return;
        }
        
        // Set initial history state to prevent BrowserHistory from going back on refresh
        // When page is refreshed, browser loses the history state, causing BrowserHistory
        // to think it's an uninitialized entry and call goBack(). We fix this by
        // ensuring the current URL has proper state.
        this.syncUrlWithPageInfo(this.currentPageInfo, true); // true = replace, don't push
        
        // Listen to browser back/forward navigation using BrowserHistory
        this.browserHistoryUnlisten = BrowserHistory.listen(() => {
            const pageInfo = this.resolvePageInfoFromUrl(window.location.href);
            
            // Update current page without modifying history
            this.currentPageInfo = pageInfo;
            
            // Notify all listeners
            for (const callback of this.onPageChangeCallbacks) {
                callback(pageInfo);
            }
        });
    }
    
    /**
     * Dispose the router service (cleanup)
     */
    public dispose(): void {
        if (this.browserHistoryUnlisten) {
            this.browserHistoryUnlisten();
            this.browserHistoryUnlisten = undefined;
        }
    }
    
    // ========================================================================
    // Navigation Methods (Type-Safe for Each Page Type)
    // ========================================================================
    
    /**
     * Navigate to home page
     * @param options - Navigation options
     */
    public navigateToHome(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo = doppeViewerPageInfoCreateHome();
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to content page
     * @param slug - URL slug
     * @param options - Navigation options
     */
    public navigateToContentPage(slug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ContentPage = {
            pageType: DoppeViewerPageType.ContentPage,
            slug: slug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to products list page
     * Uses constant path: /products
     * @param options - Navigation options
     */
    public navigateToProductsList(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ProductsList = {
            pageType: DoppeViewerPageType.ProductsList
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to product item page
     * Uses constant path: /product/:slug
     * @param productSlug - Product slug
     * @param options - Navigation options
     */
    public navigateToProductItem(productSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ProductItem = {
            pageType: DoppeViewerPageType.ProductItem,
            productSlug: productSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to blog posts list page
     * Uses constant path: /blog
     * @param options - Navigation options
     */
    public navigateToBlogPostsList(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.BlogPostsList = {
            pageType: DoppeViewerPageType.BlogPostsList
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to blog post item page
     * Uses constant path: /post/:slug
     * @param postSlug - Blog post slug
     * @param options - Navigation options
     */
    public navigateToBlogPostItem(postSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.BlogPostItem = {
            pageType: DoppeViewerPageType.BlogPostItem,
            postSlug: postSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to events list page
     * Uses constant path: /events
     * @param options - Navigation options
     */
    public navigateToEventsList(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.EventsList = {
            pageType: DoppeViewerPageType.EventsList
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to event item page
     * Uses constant path: /event/:slug
     * @param eventSlug - Event slug
     * @param options - Navigation options
     */
    public navigateToEventItem(eventSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.EventItem = {
            pageType: DoppeViewerPageType.EventItem,
            eventSlug: eventSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to services list page
     * Uses constant path: /services
     * @param options - Navigation options
     */
    public navigateToServicesList(options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ServicesList = {
            pageType: DoppeViewerPageType.ServicesList
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    /**
     * Navigate to service item page
     * Uses constant path: /service/:slug
     * @param serviceSlug - Service slug
     * @param options - Navigation options
     */
    public navigateToServiceItem(serviceSlug: string, options?: DoppeViewerRouterNavigationOptions): void {
        const pageInfo: DoppeViewerPageInfo.ServiceItem = {
            pageType: DoppeViewerPageType.ServiceItem,
            serviceSlug: serviceSlug
        };
        this.navigateToPageInfo(pageInfo, options);
    }
    
    // ========================================================================
    // URL Generation & Resolution
    // ========================================================================
    
    /**
     * Get path for a page info object
     * @param pageInfo - Page info to convert to path
     * @returns Path (e.g., '#/product/slug' or '/product/slug')
     * 
     * Routes are always root-relative for viewer (no basePath)
     */
    public getPagePath(pageInfo: DoppeViewerPageInfo): string {
        const path = this.routesManager.resolvePath(pageInfo);
        
        if (this.urlMode === 'hash' && path !== '/') {
            // Hash mode: add hash prefix
            // path is '/product/slug', we return '#/product/slug'
            return `#${path}`;
        } else {
            // Path mode: use path as-is
            // path is '/product/slug', we return '/product/slug'
            return path;
        }
    }
    
    /**
     * Resolve page info from URL
     * 
     * This method combines routes manager parsing with bymoPageProps lookup
     * to determine the actual page type, since multiple routes share the same
     * URL pattern but differ in their semantic meaning.
     * 
     * @param url - URL to parse (optional, defaults to current URL)
     * @returns Page info or home page info if parsing fails
     */
    public resolvePageInfoFromUrl(url?: string): DoppeViewerPageInfo {
        if (!url) {
            return doppeViewerPageInfoCreateHome();
        }

        const parsedPath = this.parseUrlToPath(url);
        
        // If no path or just '/', return home
        if (!parsedPath || parsedPath === '/') {
            return doppeViewerPageInfoCreateHome();
        }
        
        // Use routes manager to parse the path structure
        // This gives us the slug and itemSlug, but not the actual page type
        const routeInfo = this.routesManager.resolveRouteInfo(parsedPath, {});
        
        if (!routeInfo) {
            return doppeViewerPageInfoCreateHome();
        }
        
        // If it's home page, return immediately
        if (routeInfo.pageType === DoppeViewerPageType.Home) {
            return routeInfo;
        }
        
        // Only ContentPage needs lookup in bymoPageProps (by slug)
        // Vertical pages (products, blog, events, services) use constant paths
        // and the routes manager has already correctly identified them
        if (routeInfo.pageType === DoppeViewerPageType.ContentPage) {
            return this.resolvePageInfoWithContext(routeInfo);
        }
        
        // For all other page types (vertical pages), return routeInfo as-is
        return routeInfo;
    }
    
    /**
     * Get current page info
     * @returns Current page info or undefined if not initialized
     */
    public getCurrentPageInfo(): DoppeViewerPageInfo | undefined {
        return this.currentPageInfo;
    }
    
    /**
     * Resolve the actual inner page ID from page info
     * For vertical pages (products, blog, etc.), resolves from default pages in bymoPageProps
     * @param pageInfo - Page info to resolve ID from
     * @returns The resolved page ID, or null for home page
     */
    public resolvePageId(pageInfo: DoppeViewerPageInfo): string | null {
        return bymoEditorCurrentPageScopeGetPageId(pageInfo, this.bymoPageProps);
    }
    
    /**
     * Subscribe to page change events
     * @param callback - Function to call when page changes
     * @returns Unsubscribe function
     */
    public subscribe(callback: (pageInfo: DoppeViewerPageInfo) => void): () => void {
        this.onPageChangeCallbacks.push(callback);
        
        // Return unsubscribe function
        return () => {
            const index = this.onPageChangeCallbacks.indexOf(callback);
            if (index !== -1) {
                this.onPageChangeCallbacks.splice(index, 1);
            }
        };
    }
    
    /**
     * Compare two page info objects for equality
     * Used to prevent redundant navigation when the page info hasn't changed
     * @param pageInfo1 - First page info to compare
     * @param pageInfo2 - Second page info to compare
     * @returns true if both page infos represent the same page
     */
    public doppeViewerPageInfoEquals(
        pageInfo1: DoppeViewerPageInfo | null | undefined,
        pageInfo2: DoppeViewerPageInfo | null | undefined
    ): boolean {
        // Handle null/undefined cases
        if (pageInfo1 === pageInfo2) {
            return true;
        }
        if (!pageInfo1 || !pageInfo2) {
            return false;
        }
        
        // Different page types are not equal
        if (pageInfo1.pageType !== pageInfo2.pageType) {
            return false;
        }
        
        // Compare based on page type
        switch (pageInfo1.pageType) {
            case DoppeViewerPageType.Home:
                // Home pages are always equal if both are home
                return true;
                
            case DoppeViewerPageType.ContentPage:
                // Content pages: compare slug only
                return pageInfo1.slug === (pageInfo2 as DoppeViewerPageInfo.ContentPage).slug;
                
            case DoppeViewerPageType.ProductsList:
            case DoppeViewerPageType.BlogPostsList:
            case DoppeViewerPageType.EventsList:
            case DoppeViewerPageType.ServicesList:
                // List pages are always equal if both are the same list type
                return true;
                
            case DoppeViewerPageType.ProductItem:
                // Product item: compare productSlug
                return pageInfo1.productSlug === (pageInfo2 as DoppeViewerPageInfo.ProductItem).productSlug;
                
            case DoppeViewerPageType.BlogPostItem:
                // Blog post item: compare postSlug
                return pageInfo1.postSlug === (pageInfo2 as DoppeViewerPageInfo.BlogPostItem).postSlug;
                
            case DoppeViewerPageType.EventItem:
                // Event item: compare eventSlug
                return pageInfo1.eventSlug === (pageInfo2 as DoppeViewerPageInfo.EventItem).eventSlug;
                
            case DoppeViewerPageType.ServiceItem:
                // Service item: compare serviceSlug
                return pageInfo1.serviceSlug === (pageInfo2 as DoppeViewerPageInfo.ServiceItem).serviceSlug;
                
            default:
                // Unknown page type, consider not equal
                return false;
        }
    }
    
    // ========================================================================
    // Private Methods
    // ========================================================================
    
    /**
     * Navigate to a page info object
     * @param pageInfo - Page info to navigate to
     * @param options - Navigation options
     */
    public navigateToPageInfo(pageInfo: DoppeViewerPageInfo, options?: DoppeViewerRouterNavigationOptions): void {
        const { replace: replaceValue, updateUrl: updateUrlValue } = options ?? {};
        
        const replace = replaceValue ?? false;
        const updateUrl = updateUrlValue ?? true;
        
        // Update current page info
        this.currentPageInfo = pageInfo;
        
        // Update URL if requested
        if (updateUrl && !this.isPreviewMode) {
            // Sync URL with page info (may be skipped in preview mode)
            this.syncUrlWithPageInfo(pageInfo, replace);
        }
        else {
            for (const callback of this.onPageChangeCallbacks) {
                callback(pageInfo);
            }
        }
    }
    
    /**
     * Sync browser URL with page info
     * @param pageInfo - Page info to sync
     * @param replace - Replace history instead of push
     */
    private syncUrlWithPageInfo(pageInfo: DoppeViewerPageInfo, replace: boolean): void {
        if (this.isPreviewMode) {
            return;
        }

        // Use path only (not full URL) for browser history operations
        // BrowserHistory expects relative paths like '#/page/about' or '/page/about'
        const path = this.getPagePath(pageInfo);
        
        if (replace) {
            BrowserHistory.replace(path);
        } else {
            BrowserHistory.push(path);
        }
    }
    
    /**
     * Parse URL to path (extract hash or pathname)
     * @param url - Full URL
     * @returns Path string (e.g., '/product/slug')
     * 
     * For hash mode: extracts the hash part (e.g., '#/product/slug' -> '/product/slug')
     * For path mode: returns the pathname (e.g., '/product/slug')
     * 
     * Routes are always root-relative for viewer (no basePath)
     */
    private parseUrlToPath(url: string): string {
        const urlObject = evaluateFunction(() => {
            try {
                return new URL(url);
            } catch {
                return null;
            }
        });
        
        if (!urlObject) {
            return '/';
        }
        
        if (this.urlMode === 'hash') {
            // Extract hash part (e.g., '#/product/slug' -> '/product/slug')
            const hash = urlObject.hash;
            return hash.startsWith('#') ? hash.substring(1) : hash;
        } else {
            // Return pathname as-is (e.g., '/product/slug')
            return urlObject.pathname;
        }
    }
    
    
    /**
     * Resolve page info with context from bymoPageProps
     * 
     * This is only used for ContentPage - looks up the page by slug in bymoPageProps
     * to get the full page information.
     * 
     * @param routeInfo - Route info from routes manager (must be ContentPage)
     * @returns Resolved page info with correct type
     */
    private resolvePageInfoWithContext(routeInfo: DoppeViewerPageInfo): DoppeViewerPageInfo {
        if (!this.bymoPageProps) {
            return doppeViewerPageInfoCreateHome();
        }
        
        // This method should only be called for ContentPage
        if (routeInfo.pageType !== DoppeViewerPageType.ContentPage) {
            return routeInfo;
        }
        
        // Extract the slug from ContentPage
        const slug = routeInfo.slug;
        
        if (!slug) {
            return doppeViewerPageInfoCreateHome();
        }
        
        // Look up the page by slug
        const innerPage = bymoPagePropsGetInnerPageBySlug(this.bymoPageProps, slug);
        
        if (!innerPage) {
            return doppeViewerPageInfoCreateHome();
        }
        
        // Return the ContentPage info (pageId can be looked up via bymoPagePropsGetInnerPageBySlug)
        return {
            pageType: DoppeViewerPageType.ContentPage,
            slug: innerPage.slug
        };
    }
}

